import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { FolderModel } from '../../../../../../../typings/api/skymap/rest/v1/.common';
import { PatchFolderParameters } from '../../../../../../../typings/api/skymap/rest/v1/folder';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useFocusElement } from '../../../../hooks/use-focus-element';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { TextBox } from '../../../text-box/text-box';

interface Props {
  folder: FolderModel;
  onClose: (canceled: boolean) => void;
}

enum Status {
  Idle,
  Saving,
  Error,
}

const RenameFolderDialog = (props: Props) => {
  const { t } = useTranslation();
  const ref = useFocusElement<HTMLInputElement>(true);

  const { handleError, getErrorForPath, clearErrorForPath } =
    useErrorHandling<RequestParamsToApiPaths<PatchFolderParameters>>();
  const [status, setStatus] = React.useState(Status.Idle);
  const [name, setName] = React.useState<string>(props.folder.name);

  const saveButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      const folderService = SkyMapAxiosServiceFactory.instance.createFolderServiceV1();
      await folderService.patchFolder({
        path: { folderId: props.folder.id },
        body: { name: name },
      });

      props.onClose(false);
    } catch (err) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  const cancelButtonClick = () => {
    props.onClose(true);
  };

  const isButtonDisabled = () => {
    if (status === Status.Saving) {
      return true;
    }

    return name === props.folder.name;
  };

  return (
    <Dialog
      closeIcon={status !== Status.Saving}
      closeOnDimmerClick={true}
      width={350}
      onClose={() => props.onClose(true)}
    >
      {{
        header: t('fileManager.renameFolderDialog.title', { ns: 'components' }),
        content: (
          <>
            <Label>
              <span>{t('fileManager.renameFolderDialog.nameField', { ns: 'components' })}</span>
              <TextBox
                disabled={status === Status.Saving}
                errorMessage={getErrorForPath('body.name')}
                placeholder={props.folder.name}
                ref={ref}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  clearErrorForPath('body.name');
                }}
              />
            </Label>
          </>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={isButtonDisabled()}
                loading={status === Status.Saving}
                variant="contained"
                onClick={saveButtonClick}
              >
                {t('save', { ns: 'common' })}
              </Button>
              <Button
                disabled={status === Status.Saving}
                variant="text"
                onClick={cancelButtonClick}
              >
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Label = styled.label`
  span {
    display: block;
    margin-bottom: 0.4em;
    font-weight: 300;
    font-size: 0.9em;
  }
`;

export { RenameFolderDialog };
